<p-fluid>
    <div class="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div class="lg:col-span-7 flex flex-col gap-6">
            <div class="card flex flex-col gap-5">
                <div class="flex items-center justify-between gap-2">
                    <div class="font-semibold text-xl">Branding</div>
                    @if (dirty()) {
                    <p-button
                        label="Reset"
                        icon="pi pi-undo"
                        severity="secondary"
                        outlined
                        size="small"
                        (click)="reset()"
                    />
                    }
                </div>

                <div>
                    <div class="flex items-center justify-between mb-1">
                        <label
                            for="branding-display-name"
                            class="flex items-center gap-2 font-medium"
                            >Brand name @if (fieldDirty('DisplayName', 'Name',
                            'FullName')) {
                            <p-tag
                                severity="warn"
                                value="edited"
                                [rounded]="true"
                            />
                            }</label
                        >
                        @if (fieldDirty('DisplayName', 'Name', 'FullName')) {
                        <p-button
                            icon="pi pi-undo"
                            severity="secondary"
                            [text]="true"
                            [rounded]="true"
                            size="small"
                            title="Reset to saved"
                            ariaLabel="Reset brand name"
                            (click)="resetFields('DisplayName', 'Name', 'FullName')"
                        />
                        }
                    </div>
                    <input
                        pInputText
                        id="branding-display-name"
                        class="w-full"
                        [class.!border-amber-500]="fieldDirty('DisplayName', 'Name', 'FullName')"
                        placeholder="example: Livecazino"
                        [ngModel]="draft().DisplayName"
                        (ngModelChange)="setBrandName($event ?? '')"
                    />
                    <small class="text-muted-color">
                        The single brand name. Goes into the browser tab title
                        (<code>&lt;title&gt;</code>), social preview headline
                        (<code>og:title</code>), the <code>author</code> meta
                        tag and the admin Websites list. The in‑copy reference
                        and the legal name are <b>mirrored from this</b>
                        automatically.
                    </small>
                </div>

                <div>
                    <div class="flex items-center justify-between mb-1">
                        <span class="flex items-center gap-2 font-medium"
                            >Logo icon &amp; favicon @if
                            (fieldDirty('LogoIconUrl', 'FaviconUrl')) {
                            <p-tag
                                severity="warn"
                                value="edited"
                                [rounded]="true"
                            />
                            }</span
                        >
                        <div class="flex items-center gap-1">
                            @if (rehosting()) {
                            <i
                                class="pi pi-spinner pi-spin text-muted-color"
                            ></i>
                            } @if (fieldDirty('LogoIconUrl', 'FaviconUrl')) {
                            <p-button
                                icon="pi pi-undo"
                                severity="secondary"
                                [text]="true"
                                [rounded]="true"
                                size="small"
                                title="Reset to saved"
                                ariaLabel="Reset logo icon and favicon"
                                (click)="resetFields('LogoIconUrl', 'FaviconUrl')"
                            />
                            }
                            <app-asset-upload
                                [realmId]="realm().RealmId"
                                role="logo-icon"
                                accept="image/svg+xml,image/png,image/webp,image/x-icon,.svg,.png,.webp,.ico"
                                [maxBytes]="1048576"
                                [disabled]="uploadDisabled()"
                                [disabledReason]="uploadReason()"
                                (uploaded)="onLogoUploaded($event)"
                            />
                            <app-ai-logo-generate
                                [realmId]="realm().RealmId"
                                [brandName]="draft().DisplayName"
                                [style]="effectiveStyle()"
                                [references]="brandKit()?.references ?? []"
                                [templateId]="brandKit()?.templateId ?? ''"
                                [version]="brandKit()?.version ?? ''"
                                [disabled]="logoAiDisabled()"
                                [disabledReason]="logoAiReason()"
                                (applied)="applyLogo($event)"
                            />
                        </div>
                    </div>
                    <div class="flex items-start gap-3">
                        <div class="flex flex-col items-center gap-1">
                            <div
                                class="w-20 h-20 flex-shrink-0 rounded border border-surface-200 dark:border-surface-700 flex items-center justify-center overflow-hidden bg-surface-50 dark:bg-surface-800"
                                [class.!border-amber-500]="fieldDirty('LogoIconUrl', 'FaviconUrl')"
                            >
                                @if (assetPreviewUrl(draft().LogoIconUrl); as
                                url) {
                                <img
                                    [src]="url"
                                    alt="Logo icon preview"
                                    class="w-16 h-16 object-contain"
                                    referrerpolicy="no-referrer"
                                />
                                } @else {
                                <i
                                    class="pi pi-image text-muted-color text-xl"
                                ></i>
                                }
                            </div>
                            <span class="text-xs text-muted-color"
                                >Icon · 64px</span
                            >
                        </div>
                        <div class="flex flex-col items-center gap-1">
                            <div
                                class="w-20 h-20 flex-shrink-0 rounded border border-surface-200 dark:border-surface-700 flex items-center justify-center gap-2 bg-surface-50 dark:bg-surface-800"
                                [class.!border-amber-500]="fieldDirty('LogoIconUrl', 'FaviconUrl')"
                            >
                                @if (assetPreviewUrl(draft().FaviconUrl ||
                                draft().LogoIconUrl); as url) {
                                <img
                                    [src]="url"
                                    alt="Favicon preview, 32 pixels"
                                    class="w-8 h-8 object-contain"
                                    referrerpolicy="no-referrer"
                                />
                                <img
                                    [src]="url"
                                    alt="Favicon preview, 16 pixels"
                                    class="w-4 h-4 object-contain"
                                    referrerpolicy="no-referrer"
                                />
                                } @else {
                                <i
                                    class="pi pi-image text-muted-color text-xl"
                                ></i>
                                }
                            </div>
                            <span class="text-xs text-muted-color"
                                >Favicon · 32 / 16px</span
                            >
                        </div>
                        <div class="flex-1 min-w-0 flex flex-col gap-1">
                            <div class="flex items-center gap-1 min-w-0">
                                @if (draft().LogoIconUrl?.trim(); as path) {
                                <code
                                    class="text-xs text-muted-color truncate"
                                    [title]="path"
                                    >{{ path }}</code
                                >
                                <p-button
                                    icon="pi pi-times"
                                    severity="secondary"
                                    [text]="true"
                                    [rounded]="true"
                                    size="small"
                                    ariaLabel="Clear logo icon and favicon"
                                    title="Clear"
                                    (click)="clearLogo()"
                                />
                                } @else {
                                <span class="text-sm text-muted-color italic"
                                    >Not set</span
                                >
                                }
                            </div>
                            <small class="text-muted-color">
                                The brand mark shown next to the split logo in
                                the site header; the favicon is derived from the
                                same mark. <b>SVG preferred</b>; otherwise
                                <b>PNG with transparency</b>, square (≥256×256),
                                ≤50&nbsp;KB. Set it via <b>Upload</b> or
                                <b>Generate with AI</b>.
                            </small>
                        </div>
                    </div>
                </div>

                <div>
                    <div class="text-xs text-muted-color mb-1">
                        In‑copy preview (About page heading + body) — mirrors
                        the brand name
                    </div>
                    <div
                        class="px-4 py-3 rounded border border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-800 flex flex-col gap-2"
                    >
                        <div class="text-2xl font-bold leading-tight">
                            @if (draft().Name.trim(); as n) { About
                            <span class="text-primary">{{ n }}</span> } @else {
                            About
                            <span class="text-primary italic">Livecazino</span>
                            }
                        </div>
                        <div class="text-sm text-muted-color leading-snug">
                            @if (draft().Name.trim(); as n) {
                            <span class="text-color">{{ n }}</span> was built
                            with a simple goal — to deliver fun, fair gameplay,
                            and a community players can trust. } @else {
                            <span class="text-color italic">Livecazino</span>
                            was built with a simple goal — to deliver fun, fair
                            gameplay, and a community players can trust. }
                        </div>
                    </div>
                </div>

                <div>
                    <div class="text-xs text-muted-color mb-1">
                        Footer copyright preview — mirrors the brand name
                    </div>
                    <div
                        class="px-3 py-2 rounded border border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-800 text-sm text-center text-muted-color"
                    >
                        © {{ currentYear }} @if (draft().FullName.trim(); as fn)
                        {
                        <span class="font-medium text-color">{{ fn }}</span>
                        } @else {
                        <span class="italic">Your Brand Ltd.</span>
                        } . All rights reserved.
                    </div>
                </div>

                <div>
                    <div class="flex items-center justify-between mb-1">
                        <div class="flex items-center gap-2 font-medium">
                            Logo wordmark @if (fieldDirty('Primary',
                            'Secondary')) {
                            <p-tag
                                severity="warn"
                                value="edited"
                                [rounded]="true"
                            />
                            }
                        </div>
                        <div class="flex items-center gap-1">
                            @if (fieldDirty('Primary', 'Secondary')) {
                            <p-button
                                icon="pi pi-undo"
                                severity="secondary"
                                [text]="true"
                                [rounded]="true"
                                size="small"
                                title="Reset to saved"
                                ariaLabel="Reset logo wordmark"
                                (click)="resetFields('Primary', 'Secondary')"
                            />
                            }
                            <p-button
                                label="Auto‑split"
                                icon="pi pi-bolt"
                                severity="secondary"
                                [text]="true"
                                size="small"
                                [disabled]="!draft().DisplayName.trim()"
                                (click)="autoSplitLogo()"
                            />
                        </div>
                    </div>
                    <div class="grid grid-cols-2 gap-4">
                        <div>
                            <label
                                for="branding-primary"
                                class="block font-medium mb-1"
                                >Logo — primary half</label
                            >
                            <input
                                pInputText
                                id="branding-primary"
                                class="w-full"
                                [class.!border-amber-500]="fieldDirty('Primary')"
                                placeholder="example: Live"
                                [ngModel]="draft().Primary"
                                (ngModelChange)="setField('Primary', $event ?? '')"
                            />
                            <small class="text-muted-color">
                                First part of the split logo, rendered in the
                                brand's <b>primary color</b>.
                            </small>
                        </div>
                        <div>
                            <label
                                for="branding-secondary"
                                class="block font-medium mb-1"
                                >Logo — secondary half</label
                            >
                            <input
                                pInputText
                                id="branding-secondary"
                                class="w-full"
                                [class.!border-amber-500]="fieldDirty('Secondary')"
                                placeholder="example: cazino"
                                [ngModel]="draft().Secondary"
                                (ngModelChange)="setField('Secondary', $event ?? '')"
                            />
                            <small class="text-muted-color">
                                Second part of the logo, rendered in
                                <b>default text color</b>. Together with Primary
                                they form the visible mark.
                            </small>
                        </div>
                    </div>
                    <div class="mt-3">
                        <div class="text-xs text-muted-color mb-1">
                            Logo preview (logo icon + split mark, as it appears
                            in the site header)
                        </div>
                        <div
                            class="px-4 py-3 rounded border border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-800 flex items-center gap-2"
                        >
                            @if (assetPreviewUrl(draft().LogoIconUrl ||
                            draft().FaviconUrl); as mark) {
                            <img
                                [src]="mark"
                                alt=""
                                aria-hidden="true"
                                class="h-7 w-7 object-contain flex-shrink-0"
                                referrerpolicy="no-referrer"
                            />
                            } @else {
                            <i
                                class="pi pi-image text-muted-color text-xl flex-shrink-0"
                            ></i>
                            } @if (draft().Primary || draft().Secondary) {
                            <span class="text-lg font-bold leading-none">
                                <span class="text-primary"
                                    >{{ draft().Primary }}</span
                                ><span>{{ draft().Secondary }}</span>
                            </span>
                            } @else {
                            <span
                                class="text-lg font-bold leading-none italic text-muted-color"
                            >
                                <span class="text-primary">Live</span>cazino
                            </span>
                            <span class="text-xs text-muted-color italic"
                                >(placeholder — fill the fields above)</span
                            >
                            }
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="lg:col-span-5">
            <div class="card flex flex-col gap-5 lg:sticky lg:top-20">
                <div>
                    <div class="font-semibold text-xl">Social preview</div>
                    <p class="text-sm text-muted-color m-0 mt-1">
                        How the site appears when its link is shared in chats
                        and social feeds. Edit the two fields below and watch
                        the card update live.
                    </p>
                </div>

                <div>
                    <div class="flex items-center justify-between mb-1">
                        <label
                            for="branding-description"
                            class="flex items-center gap-2 font-medium"
                            >SEO description @if (fieldDirty('Description')) {
                            <p-tag
                                severity="warn"
                                value="edited"
                                [rounded]="true"
                            />
                            }</label
                        >
                        @if (fieldDirty('Description')) {
                        <p-button
                            icon="pi pi-undo"
                            severity="secondary"
                            [text]="true"
                            [rounded]="true"
                            size="small"
                            title="Reset to saved"
                            ariaLabel="Reset SEO description"
                            (click)="resetFields('Description')"
                        />
                        }
                        <app-ai-seo-assist
                            [text]="draft().Description"
                            [maxLength]="160"
                            [brandName]="draft().DisplayName || draft().Name"
                            [domain]="realm().Domain"
                            [vertical]="vertical()"
                            [style]="effectiveStyle()"
                            [contextDisplayName]="draft().DisplayName"
                            [contextName]="draft().Name"
                            [disabled]="textAiDisabled()"
                            [disabledReason]="textAiReason()"
                            (applied)="setField('Description', $event)"
                        />
                    </div>
                    <input
                        pInputText
                        id="branding-description"
                        class="w-full"
                        [class.!border-amber-500]="fieldDirty('Description')"
                        placeholder="example: Livecazino - Your social gaming platform"
                        [ngModel]="draft().Description"
                        (ngModelChange)="setField('Description', $event ?? '')"
                    />
                    <small class="text-muted-color">
                        Short one‑sentence blurb (~120–160 chars). Goes into
                        <code>&lt;meta name="description"&gt;</code> and
                        <code>og:description</code> — what users see in Google
                        results and link previews on Slack / Twitter / Facebook.
                    </small>
                </div>

                <div>
                    <div class="flex items-center justify-between mb-1">
                        <span class="flex items-center gap-2 font-medium"
                            >Social preview image @if (fieldDirty('OgImageUrl'))
                            {
                            <p-tag
                                severity="warn"
                                value="edited"
                                [rounded]="true"
                            />
                            }</span
                        >
                        <div class="flex items-center gap-1">
                            @if (rehosting()) {
                            <i
                                class="pi pi-spinner pi-spin text-muted-color"
                            ></i>
                            } @if (fieldDirty('OgImageUrl')) {
                            <p-button
                                icon="pi pi-undo"
                                severity="secondary"
                                [text]="true"
                                [rounded]="true"
                                size="small"
                                title="Reset to saved"
                                ariaLabel="Reset social preview image"
                                (click)="resetFields('OgImageUrl')"
                            />
                            }
                            <app-asset-upload
                                [realmId]="realm().RealmId"
                                role="og"
                                accept="image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp"
                                [maxBytes]="5242880"
                                [disabled]="uploadDisabled()"
                                [disabledReason]="uploadReason()"
                                (uploaded)="setField('OgImageUrl', $event)"
                            />
                            <app-ai-og-generate
                                [realmId]="realm().RealmId"
                                [title]="draft().DisplayName"
                                [defaultTagline]="draft().Description"
                                [ogBasePlate]="brandKit()?.ogBasePlate ?? null"
                                [disabled]="ogAiDisabled()"
                                [disabledReason]="ogAiReason()"
                                (applied)="applyOg($event)"
                            />
                        </div>
                    </div>
                    <div class="flex items-center gap-1 min-w-0">
                        @if (draft().OgImageUrl?.trim(); as path) {
                        <code
                            class="text-xs text-muted-color truncate"
                            [title]="path"
                            >{{ path }}</code
                        >
                        <p-button
                            icon="pi pi-times"
                            severity="secondary"
                            [text]="true"
                            [rounded]="true"
                            size="small"
                            ariaLabel="Clear social preview image"
                            title="Clear"
                            (click)="setField('OgImageUrl', null)"
                        />
                        } @else {
                        <span class="text-sm text-muted-color italic"
                            >Not set</span
                        >
                        }
                    </div>
                    <small class="text-muted-color">
                        A <b>1200×630</b> PNG / JPG used by
                        <code>og:image</code> and <code>twitter:image</code> —
                        the picture that shows when the site is linked in Slack,
                        Twitter, Facebook etc. Shown in the preview card below;
                        when not set, the site falls back to the default. Set it
                        via <b>Upload</b> or <b>Generate with AI</b>.
                    </small>
                </div>

                <app-branding-preview
                    [branding]="draft()"
                    [domain]="realm().Domain"
                />
            </div>
        </div>
    </div>
</p-fluid>
